import { useState } from "react";

export const levelOf = (r) => {
  if (r.score > 15 || r.rpn > 500) return { cls: "b-critical", label: "Critical" };
  if (r.score > 8  || r.rpn > 200) return { cls: "b-high",     label: "High"     };
  if (r.score > 4)                  return { cls: "b-medium",   label: "Medium"   };
  return                                   { cls: "b-low",      label: "Low"      };
};

const scoreColorClass = (score) =>
  score > 15 ? "score-high" : score > 8 ? "score-warn" : "score-ok";

async function fetchAIMitigation(risk) {
  const prompt = `You are a risk management consultant.
Risk: "${risk.name}" | Category: ${risk.cat} | Score: ${risk.score} | RPN: ${risk.rpn}
Write a concise 2–3 sentence mitigation strategy. Be specific and actionable. Return only the text.`;

  const res = await fetch("https://api.groq.com/openai/v1/chat/completions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${process.env.REACT_APP_GROQ_API_KEY}`
    },
    body: JSON.stringify({
      model: "llama-3.3-70b-versatile",
      messages: [
        { role: "system", content: "You are a risk management expert." },
        { role: "user", content: prompt }
      ],
      temperature: 0.3,
      max_tokens: 200
    }),
  });

  const data = await res.json();
  console.log("GROQ RESPONSE:", data);

  // 🔥 Handle errors properly
  if (!res.ok) {
    throw new Error(data.error?.message || "Groq API error");
  }

  const text = data?.choices?.[0]?.message?.content;

  if (!text) {
    throw new Error("Empty AI response");
  }

  return text.trim();
}

export default function RiskTable({ risks, deleteRisk, updateMit, showToast }) {
  const [sortKey, setSortKey]   = useState("score");
  const [sortDir, setSortDir]   = useState(-1);
  const [filter, setFilter]     = useState("");
  const [openMit, setOpenMit]   = useState({});   // id -> bool
  const [aiLoading, setAiLoading] = useState({}); // id -> bool

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => -d);
    else { setSortKey(key); setSortDir(-1); }
  };

  const toggleMit = (id) => setOpenMit((p) => ({ ...p, [id]: !p[id] }));

  const handleAIMit = async (risk) => {
    setAiLoading((p) => ({ ...p, [risk.id]: true }));
    try {
      const text = await fetchAIMitigation(risk);
      updateMit(risk.id, text);
      setOpenMit((p) => ({ ...p, [risk.id]: true }));
      showToast("Mitigation generated");
    } catch {
      showToast("Could not reach Claude API");
    } finally {
      setAiLoading((p) => ({ ...p, [risk.id]: false }));
    }
  };

  const q = filter.toLowerCase();
  const filtered = [...risks]
    .filter((r) =>
      r.name.toLowerCase().includes(q) ||
      r.cat.toLowerCase().includes(q)  ||
      (r.owner || "").toLowerCase().includes(q)
    )
    .sort((a, b) => {
      const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
      return typeof av === "string" ? av.localeCompare(bv) * sortDir : (av - bv) * sortDir;
    });

  const SortArrow = ({ k }) => (
    <span className="sort-arrow">
      {sortKey === k ? (sortDir === -1 ? "↓" : "↑") : "↕"}
    </span>
  );

  const cols = [
    { key: "name",        label: "Name"     },
    { key: null,          label: "Category" },
    { key: null,          label: "Owner"    },
    { key: "prob",        label: "P"        },
    { key: "imp",         label: "I"        },
    { key: "score",       label: "Score"    },
    { key: "rpn",         label: "RPN"      },
    { key: null,          label: "Level"    },
    { key: null,          label: ""         },
  ];

  return (
    <div className="panel">
      <div className="panel-toolbar">
        <input
          className="search-input"
          placeholder="Filter risks…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="count-badge">{filtered.length} risk{filtered.length !== 1 ? "s" : ""}</span>
        </div>
      </div>

      <table className="risk-table">
        <thead>
          <tr>
            {cols.map(({ key, label }) => (
              <th key={label} onClick={() => key && toggleSort(key)}>
                {label}{key && <SortArrow k={key} />}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 && (
            <tr>
              <td colSpan={9} className="empty-state">
                No risks yet — add one in the sidebar or use AI Suggest ✦
              </td>
            </tr>
          )}
          {filtered.map((r) => {
            const lv = levelOf(r);
            const mitOpen = openMit[r.id];
            return (
              <>
                <tr key={r.id}>
                  <td>
                    <div className="risk-name-wrap">
                      <div className="risk-name">{r.name}</div>
                      <span className="mit-toggle" onClick={() => toggleMit(r.id)}>
                        {r.mit ? "📝 notes" : "+ add notes"}
                      </span>
                    </div>
                  </td>
                  <td><span className="cat-pill">{r.cat}</span></td>
                  <td style={{ color: "var(--soft)", fontSize: 12 }}>{r.owner || "—"}</td>
                  <td className="score-mono">{r.prob}</td>
                  <td className="score-mono">{r.imp}</td>
                  <td className={`score-mono ${scoreColorClass(r.score)}`}>{r.score}</td>
                  <td className="score-mono">{r.rpn}</td>
                  <td><span className={`badge ${lv.cls}`}>{lv.label}</span></td>
                  <td>
                    <button className="del-btn" onClick={() => deleteRisk(r.id)} title="Delete">×</button>
                  </td>
                </tr>

                {/* Mitigation expansion row */}
                {mitOpen && (
                  <tr key={`mit-${r.id}`}>
                    <td colSpan={9} className="mit-expand-cell">
                      <div className="mit-expand-inner">
                        <textarea
                          placeholder="Add mitigation notes…"
                          value={r.mit || ""}
                          disabled={aiLoading[r.id]}
                          onChange={(e) => updateMit(r.id, e.target.value)}
                        />
                        <button
                          className="btn btn-ai"
                          style={{ whiteSpace: "nowrap", padding: "6px 12px", fontSize: 11 }}
                          onClick={() => handleAIMit(r)}
                          disabled={aiLoading[r.id]}
                        >
                          {aiLoading[r.id] ? "…" : "✦ AI"}
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}